import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { staffServiceApi, ApiError } from '../api';
import { mapApiService, toServiceRequest } from './servicesStore';
import type { Service } from './servicesStore';

/**
 * Services for the Staff portal. Mirrors `servicesStore` but hits the
 * `/staff/service` endpoints, which scope everything to the staff member's
 * assigned location — hence no locationId anywhere in this store.
 */
interface StaffServicesState {
	services: Service[];
	isLoading: boolean;
	error: string | null;
	fetchServices: () => Promise<void>;
	addService: (service: Service) => Promise<void>;
	updateService: (id: string, service: Service) => Promise<void>;
	deleteService: (id: string) => Promise<void>;
	clearServices: () => void;
}

const useStaffServicesStore = create<StaffServicesState>()(
	devtools(
		set => ({
			services: [],
			isLoading: false,
			error: null,

			fetchServices: async () => {
				set({ isLoading: true, error: null });

				try {
					const apiServices = await staffServiceApi.list();
					set({
						services: (apiServices ?? []).map(mapApiService),
						isLoading: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						error: apiError.message || 'Failed to load services',
						isLoading: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Staff services fetch error:', apiError);
					}
				}
			},

			addService: async (service: Service) => {
				const created = await staffServiceApi.create(toServiceRequest(service));
				set(state => ({
					services: [...state.services, mapApiService(created)],
				}));
			},

			updateService: async (id: string, service: Service) => {
				// Categories are managed from the provider portal only, so the
				// staff editor never sets them — send back whatever the service
				// already had rather than blanking it.
				const existing = useStaffServicesStore.getState().services.find(s => s.id === id);
				const updated = await staffServiceApi.update(Number(id), {
					...toServiceRequest(service),
					category: service.category ?? existing?.category ?? null,
					categoryId: service.categoryId ?? existing?.categoryId ?? null,
				});
				set(state => ({
					services: state.services.map(s => (s.id === id ? mapApiService(updated) : s)),
				}));
			},

			deleteService: async (id: string) => {
				await staffServiceApi.delete(Number(id));
				set(state => ({
					services: state.services.filter(s => s.id !== id),
				}));
			},

			clearServices: () => {
				set({ services: [], isLoading: false, error: null });
			},
		}),
		{ name: 'staff-services-store' }
	)
);

export default useStaffServicesStore;
